import styled from "@emotion/styled";
import { border, size } from "polished";
import React, { FormEvent } from "react";

import { createInlineSSUButtonStyles } from "@brexhq/next/src/components/shared/styles/Button.elements";
import TextStyles from "@brexhq/shared/components/Typography/styles";

const FormSubmit = styled.button(createInlineSSUButtonStyles());

const InlineForm = styled.form<{ hasError: boolean }>(
  ({ hasError, theme }) => ({
    ...border(
      theme.borders.width.default,
      "solid",
      hasError ? theme.colors.negative.default : theme.colors.border.default,
    ),
    ...size(64, 480),
    position: "relative",
    background: theme.colors.background.primary,
    display: "flex",
    borderRadius: theme.borders.radius.default,
    justifyContent: "space-between",
    padding: "8px",
    [theme.breakpoints.MED]: {
      ...size(48, 400),
      padding: "6px",
    },
    [theme.breakpoints.SML]: {
      width: "min(100%, 280px)",
      padding: "5px",
    },
    "&:focus-within": {
      borderColor: theme.colors.border.focused,
    },
  }),
);

const FormInput = styled.input(({ theme: { breakpoints } }) => ({
  position: "relative",
  background: "none",
  border: "none",
  paddingLeft: 10,
  width: "100%",
  ...TextStyles.Body1,
  [breakpoints.MED]: {
    maxWidth: "min(100%, 220px)",
  },
  [breakpoints.SML]: {
    maxWidth: "min(100%, 220px)",
  },
  "&:focus": {
    // Focus handled via focus-within above
    outline: "none",
  },
  "&:invalid": {
    // remove red outline from single input - will set on the wrapper itself
    boxShadow: "none",
  },
}));

export interface InlineSSUFormProps {
  type: "EMAIL" | "PREF_TOKEN";
  id?: string;
  value: string;
  handleFormInput: (evt: React.FormEvent<HTMLInputElement>) => void;
  handleFormSubmit: (evt: FormEvent) => void;
  hasError: boolean;
  submitButtonLabel?: string;
}

const MobileFormWrapper = styled(InlineForm)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.SML]: {
    display: "flex",
  },
}));

const DesktopFormWrapper = styled(InlineForm)(({ theme }) => ({
  [theme.breakpoints.SML]: {
    display: "none",
  },
}));

export const DesktopForm: React.FC<InlineSSUFormProps> = ({
  type,
  id,
  value,
  handleFormInput,
  handleFormSubmit,
  hasError,
  submitButtonLabel,
}) => (
  <DesktopFormWrapper
    id={id}
    data-testid="singleSSU-form-desktop"
    onSubmit={handleFormSubmit}
    hasError={hasError}
    noValidate
  >
    <FormInput
      data-testid="singleSSU-formInput-desktop"
      onChange={handleFormInput}
      value={value}
      type={type === "EMAIL" ? "email" : "text"}
      placeholder={
        type === "EMAIL" ? "Enter your work email" : "Enter your RSVP Code"
      }
    />
    <FormSubmit data-testid="singleSSU-formSubmit-desktop" type="submit">
      {submitButtonLabel ?? "->"}
    </FormSubmit>
  </DesktopFormWrapper>
);

export const MobileForm: React.FC<InlineSSUFormProps> = ({
  type,
  id,
  value,
  handleFormInput,
  handleFormSubmit,
  hasError,
}) => (
  <MobileFormWrapper
    id={id}
    data-testid="singleSSU-form-mobile"
    onSubmit={handleFormSubmit}
    hasError={hasError}
    noValidate
  >
    <FormInput
      data-testid="singleSSU-formInput-mobile"
      onChange={handleFormInput}
      value={value}
      type={type === "EMAIL" ? "email" : "text"}
      placeholder={type === "EMAIL" ? "Your email" : "RSVP Code"}
    />
    <FormSubmit data-testid="singleSSU-formSubmit-mobile" type="submit">
      {"->"}
    </FormSubmit>
  </MobileFormWrapper>
);
